import React, { useState, useEffect, useRef, useMemo } from "react";
import { localToday } from "./utils/localDate";
import { Loader2, AlertTriangle, Globe } from "lucide-react";
import { Analytics } from "@vercel/analytics/react";
import {
    Routes,
    Route,
    useNavigate,
    useLocation,
    Navigate,
} from "react-router-dom";
import { fetchOwnSupportTickets } from "./lib/appBootstrapData";
import pms from "./services/pms";
import { withProgress } from "./stores/fetchProgressStore";
import { useTranslation } from "react-i18next";
import {
    CustomDateInput,
    MultiSelectDropdown,
    ToastProvider,
} from "./utils.tsx";
import {
    NotificationListener,
    NotificationHistoryDrawer,
} from "./notifications.tsx";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { useUnreadNotifications, useBrowserTabNotification } from "./hooks.ts";
import { useUsageTracker } from "./hooks/useUsageTracker";
import { useHRData } from "./hooks/useHRData";
import { useGlobalPresence } from "./hooks/useGlobalPresence";
import { useFilterParam, useDateFilterParam } from "./hooks/usePersistedFilters";
import { createRealtimeStore } from "./lib/realtimeStore";
import { useUIStore } from "./stores/uiStore";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { ROLES, TAB_TITLES, PATCH_NOTES } from "./constants";
import InstallPwaButton from "./components/InstallPwaButton";
import { NavItemsList } from "./app/AppNav";
import { AppSidebar } from "./app/AppSidebar";
import { AppHeader } from "./app/AppHeader";
import { SystemLogsView } from "./app/SystemLogsView";
import { isChunkLoadError, recoverFromChunkError } from "./lib/chunkErrorRecovery";
import type { AppUser } from "./hooks/useHRData";

/** Shape of a tenants-table data JSONB blob relevant to the app shell. */
export interface TenantConfig {
  allowedProperties?: Array<string | { id?: string }>;
  features?: Record<string, boolean>;
  onboardingCompleted?: boolean;
  customIframeName?: string;
  customIframeUrl?: string;
  [key: string]: unknown;
}
import { useHandovers } from "./features/handover/hooks/useHandovers";
import { HandoverGlobalBanner } from "./features/handover/HandoverGlobalBanner";
import { getRoleData, getSafeIframeUrl } from "./app/appHelpers";
// Messenger unread badge for the sidebar. Imported DIRECTLY (not through the
// lazy `./features/chat` barrel) so the app shell does not pull the whole
// ChatView chunk on every route — only the small hook + its query.
import { useChatUnread } from "./features/chat/hooks/useChatUnread";
import {
  ExplorerView,
  TracesView,
  HandoverView,
  HRView,
  ReservationsView,
  ReservationsRoute,
  RoomRackView,
  AvailabilityView,
  AnalyticsView,
  HousekeepingView,
  SalesView,
  FundsachenView,
  HaustechnikView,
  InternalResourcesView,
  InventoryView,
  KassenbuchView,
  BuchhaltungView,
  XRechnungView,
  AnleitungenView,
  GroupsView,
  OffersView,
  AdminDashboardView,
  AutomationsView,
  WorkflowsView,
  ChatView,
  DashboardView,
  PatchNotesModal,
  UserProfilePreferencesModal,
  UsersView,
  SupportTicketModal,
  FileViewerModal,
} from "./app/lazyRoutes";

// Code-split the heavy view components so they are loaded on-demand instead
// of being bundled into the initial chunk. lazyWithRetry survives stale-chunk
// errors after a deploy (retries the import, then reloads once for fresh
// asset hashes) — see src/lib/lazyWithRetry.ts.


export default function AuthedApp({
    user,
    userRole,
    isApaleoAuthed,
    setIsApaleoAuthed,
    isOffline,
    tenantConfig,
    setTenantConfig,
    globalAdmins,
    signOut,
    getToken,
}: {
    user: AppUser;
    userRole: string | null;
    isApaleoAuthed: boolean;
    setIsApaleoAuthed: (v: boolean) => void;
    isOffline: boolean;
    tenantConfig: TenantConfig | null;
    setTenantConfig: (config: TenantConfig) => void;
    globalAdmins: string[];
    signOut: () => Promise<void>;
    getToken: () => Promise<string | null>;
}) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const activeTab =
        location.pathname === "/"
            ? "dashboard"
            : location.pathname.substring(1).split("/")[0];

    // ── UI state via Zustand store ─────────────────────────────────
    const isMobileMenuOpen = useUIStore(s => s.isMobileMenuOpen);
    const setIsMobileMenuOpen = useUIStore(s => s.setMobileMenuOpen);
    const isSidebarCollapsed = useUIStore(s => s.isSidebarCollapsed);
    const setIsSidebarCollapsed = useUIStore(s => s.setSidebarCollapsed);

    // Initialize sidebar state from localStorage (Zustand store starts at default)
    useEffect(() => {
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem("adapthub_sidebar_collapsed") === "true";
            setIsSidebarCollapsed(saved);
        }
    }, [setIsSidebarCollapsed]);

    useEffect(() => {
        localStorage.setItem("adapthub_sidebar_collapsed", String(isSidebarCollapsed));
    }, [isSidebarCollapsed]);

    const [apiError, setApiError] = useState("");

    const isHistoryOpen = useUIStore(s => s.isHistoryOpen);
    const setIsHistoryOpen = useUIStore(s => s.setHistoryOpen);
    const isPatchNotesOpen = useUIStore(s => s.isPatchNotesOpen);
    const setIsPatchNotesOpen = useUIStore(s => s.setPatchNotesOpen);
    const hasNewPatchNote = useUIStore(s => s.hasNewPatchNote);
    const setHasNewPatchNote = useUIStore(s => s.setHasNewPatchNote);
    const hasUnreadSupportTickets = useUIStore(s => s.hasUnreadSupportTickets);
    const setHasUnreadSupportTickets = useUIStore(s => s.setHasUnreadSupportTickets);
    const theme = useUIStore(s => s.theme);
    const toggleTheme = useUIStore(s => s.toggleTheme);

    // ── Usage analytics (2026-10-22) ───────────────────────────────
    // One route/search observer at the app shell records menu + subtab
    // navigation into app_usage_events (batched, throttled, staff-only via
    // RLS). Tracking must never block UX — the hook is fire-and-forget.
    useUsageTracker(user);

    // Standalone internal messenger (spec: plans/internal-chat-messenger-spec.md):
    // the unread badge derives from chat_conversation_members.last_read_at, NOT
    // the notifications pipeline.
    //
    // The count is computed HERE (app shell) rather than reported up from
    // ChatView, because the shell renders on every route — a badge that only
    // updated while /chat was open would be useless. `useChatUnread` re-uses
    // the SAME TanStack Query cache entry as the open ChatView
    // (queryKeys.messenger.conversations), so this costs no extra fetch and
    // both surfaces stay in sync. See the hook call below `allowedTabs`.

    // Shift Handover (Schichtübergabe): badge + banner source. Shares the
    // query cache with HandoverView / HandoverGlobalBanner — one fetch total.
    // (Adapt at the call site: AppUser.tenantId is string|null, the hook's
    // HandoverUser wants string|undefined.)
    const { incoming: pendingHandovers } = useHandovers(
        { tenantId: user?.tenantId ?? undefined, id: user?.id, role: user?.role },
    );
    const hasPendingHandover = pendingHandovers.length > 0;
    const hasUnreadUserReplies = useUIStore(s => s.hasUnreadUserReplies);
    const setHasUnreadUserReplies = useUIStore(s => s.setHasUnreadUserReplies);
    const userTickets = useUIStore(s => s.userTickets);
    const setUserTickets = useUIStore(s => s.setUserTickets);
    const readUpdateTrigger = useUIStore(s => s.readUpdateTrigger);
    const setReadUpdateTrigger = useUIStore(s => s.incrementReadUpdateTrigger);

    useEffect(() => {
        const latestVersion = PATCH_NOTES[0]?.version;
        const lastViewed = localStorage.getItem("adapthub_last_viewed_patch_version");
        if (latestVersion && lastViewed !== latestVersion) {
            setHasNewPatchNote(true);
        }
    }, [setHasNewPatchNote]);

    const handleOpenPatchNotes = () => {
        setIsPatchNotesOpen(true);
        setHasNewPatchNote(false);
        const latestVersion = PATCH_NOTES[0]?.version;
        if (latestVersion) {
            localStorage.setItem("adapthub_last_viewed_patch_version", latestVersion);
        }
    };

    const isPreferencesOpen = useUIStore(s => s.isPreferencesOpen);
    const setIsPreferencesOpen = useUIStore(s => s.setPreferencesOpen);
    const isSupportOpen = useUIStore(s => s.isSupportOpen);
    const setIsSupportOpen = useUIStore(s => s.setSupportOpen);
    const globalSupportTicketIdToOpen = useUIStore(s => s.globalSupportTicketIdToOpen);
    const setGlobalSupportTicketIdToOpen = useUIStore(s => s.setGlobalSupportTicketIdToOpen);

    // Push registration: the hook auto-registers only when permission is
    // ALREADY granted. requestPushPermission() is the gesture-driven path,
    // invoked from the notification bell below — iOS ignores a permission
    // request that is not tied to a user interaction, which previously left
    // iPhone users with no FCM token and therefore no push notifications.
    const { requestPermission: requestPushPermission } = usePushNotifications({ ...user, tenantId: user.tenantId ?? undefined });

    const unreadCount = useUnreadNotifications({ ...user, tenantId: user.tenantId ?? undefined });
    useBrowserTabNotification(unreadCount);

    // Memoize date-derived values so we don't allocate a new Date and re-format
    // the locale string on every render.
    const todayIso = localToday();
    // Date filters reset to today on mount — stale URL/localStorage values from
    // previous sessions are ignored and cleared (useDateFilterParam).
    const [dashboardDateFrom, setDashboardDateFrom] = useDateFilterParam("dashboard", "dateFrom", todayIso);
    const [dashboardDateTo, setDashboardDateTo] = useDateFilterParam("dashboard", "dateTo", todayIso);
    const [dashboardDateFilter, setDashboardDateFilter] = useFilterParam("dashboard", "filter", "Stay");
    const [dashboardSearchTerm, setDashboardSearchTerm] = useFilterParam("dashboard", "q", "");

    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const { clockStatus, displayMs } = useHRData(user);

    // Track this user on the global presence channel so the admin
    // dashboard can see who is currently online across all tenants.
    useGlobalPresence({ ...user, tenantId: user.tenantId ?? undefined });

    const formattedDate = (() => {
        const now = new Date();
        const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
        const dayName = now.toLocaleDateString(locale, { weekday: 'long' }).toUpperCase();
        const monthName = now.toLocaleDateString(locale, { month: 'long' }).toUpperCase();
        const dayNum = now.getDate();
        return locale === 'de-DE'
            ? `${dayName}, ${dayNum}. ${monthName}`
            : `${dayName}, ${monthName} ${dayNum}`;
    })();

    const formatClockTime = (ms: number) => {
        if (!ms || ms <= 0) return '00:00:00';
        const totalSeconds = Math.floor(ms / 1000);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const clockLabels = { in: t('hr.clock.statusIn'), break: t('hr.clock.statusBreak'), out: t('hr.clock.statusOut') };
    const clockColors = { in: 'text-accent', break: 'text-amber-500', out: 'text-muted' };
    const badgeBg = { in: 'bg-[#F5F3FF]', break: 'bg-amber-50', out: 'bg-surface' };
    // `clockStatus` is `'in' | 'out' | 'break' | string` (HR state row is untyped
    // at the DB boundary); the lookup maps only narrow it to the three known keys.
    const clockKey = (clockStatus === 'in' || clockStatus === 'break' || clockStatus === 'out') ? clockStatus : 'out' as const;

    const expandedGroups = useUIStore(s => s.expandedGroups);
    const setExpandedGroups = useUIStore(s => s.setExpandedGroups);

    // expandedGroups is restored synchronously INSIDE the store
    // (uiStore.ts loadExpandedGroups). Do NOT re-add a post-mount restore
    // effect here: under StrictMode it runs twice with stale closures and the
    // persistence effect below would then overwrite the restored value with {}
    // on every reload.

    useEffect(() => {
        // Write only a real object — JSON.stringify(undefined) produces the
        // literal string "undefined", which JSON.parse rejects on the next
        // mount (the crash this guard pair fixes).
        if (expandedGroups && typeof expandedGroups === "object") {
            localStorage.setItem(
                "adapthub_expanded_groups",
                JSON.stringify(expandedGroups),
            );
        }
    }, [expandedGroups]);

    const toggleGroup = (groupName: string) => {
        if (!groupName || typeof groupName !== "string") return;
        // Read the freshest map from the store instead of the render closure so
        // two rapid clicks can never compute from a stale value.
        const prev = useUIStore.getState().expandedGroups || {};
        const exists = Object.prototype.hasOwnProperty.call(prev, groupName);
        // A group absent from the map renders as expanded (AppNav defaults to
        // true), so the FIRST click must collapse it -> false.
        setExpandedGroups({
            ...prev,
            [groupName]: exists ? !prev[groupName] : false,
        });
    };

    // Dep stabilization (P.2/P.3): the effect only reads user?.email — depending on
    // the whole `user` object refired the /api/admin/support-tickets fetch on every
    // setUser() identity change. Key on the email primitive instead.
    const adminEmail = user?.email;
    useEffect(() => {
        // Gate on the runtime globalAdmins list (admin_config.data.globalAdmins) —
        // the same source the admin-tab gate below uses. The previous hardcoded
        // email list meant the red ticket dot never lit for admins added to
        // admin_config after this code was written.
        const isGlobalAdmin = userRole === "admin" && !!adminEmail && globalAdmins.includes(adminEmail.toLowerCase());
        if (!isGlobalAdmin) {
            setHasUnreadSupportTickets(false);
            return;
        }

        const checkTickets = async () => {
            try {
                const token = await getToken();
                if (!token) return;
                const res = await fetch("/api/admin/support-tickets", {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (!res.ok) return;
                const data = await res.json();
                const tickets = data.tickets || [];
                const hasOpen = tickets.some((t: { status?: string }) => t.status === "open");
                setHasUnreadSupportTickets(hasOpen);
            } catch (err) {
                console.warn("Failed to check support tickets for unread status:", err);
            }
        };

        checkTickets();
    }, [adminEmail, userRole, globalAdmins, getToken, setHasUnreadSupportTickets]);

    // Sync user's own support tickets in real-time
    useEffect(() => {
        if (!user) {
            setUserTickets([]);
            return;
        }

        const activeTenantId = user.tenantId;

        const fetchTickets = async () => {
            const { data: rows } = await fetchOwnSupportTickets(activeTenantId, user.id);
            setUserTickets(rows || []);
        };
        fetchTickets();
        // Realtime handled by centralized realtimeStore
    }, [user, setUserTickets]);

    const _migrated_old_tickets = [];

    // Check for unread replies whenever tickets or read states change
    useEffect(() => {
        const readStates = JSON.parse(localStorage.getItem("adapthub_read_tickets") || "{}");
        const unread = userTickets.some((ticket) => {
            const t = ticket as any;
            if (!t.responses || t.responses.length === 0) return false;
            const lastResponse = t.responses[t.responses.length - 1];
            if (lastResponse.sender !== "admin") return false;

            const lastReadTime = readStates[t.id] || "";
            return lastResponse.createdAt > lastReadTime;
        });
        setHasUnreadUserReplies(unread);
    }, [userTickets, readUpdateTrigger, setHasUnreadUserReplies]);

    // Listen for custom read update events from the Support modal
    useEffect(() => {
        const handleReadUpdate = () => {
            setReadUpdateTrigger();
        };
        window.addEventListener("support_tickets_read_update", handleReadUpdate);
        return () => window.removeEventListener("support_tickets_read_update", handleReadUpdate);
    }, [setReadUpdateTrigger]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(dashboardSearchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [dashboardSearchTerm]);

    // Keep the latest activeTab in a ref so the reservation fetch effect does
    // NOT re-run on every tab navigation. The fetch only depends on the actual
    // data inputs (dates, filter type, search) — tab switches just read the
    // current value via the ref.
    const activeTabRef = useRef(activeTab);
    useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

    // ── Reservations via TanStack Query ────────────────────────────
    // Replaces manual useState + useEffect fetch with cached, debounced query.
    const { data: reservations, isLoading: isLoadingReservations, error: reservationsError } = useQuery({
        queryKey: ['reservations', 'dashboard', dashboardDateFrom, dashboardDateTo, dashboardDateFilter, debouncedSearchTerm],
        queryFn: async () => {
            // Global FetchProgressBar: per-page ticks while pages stream in
            // (owner request 2026-10-08 — spinner reads as "crashed" during
            // long pulls).
            const progress = withProgress('fetchProgress.reservations');
            try {
                const fetchFilter = activeTabRef.current === "hk" ? "Stay" : dashboardDateFilter;
                const resData = await pms.fetchReservations(
                    dashboardDateFrom,
                    dashboardDateTo,
                    fetchFilter,
                    "",
                    debouncedSearchTerm,
                    null,
                    50,
                    { onProgress: progress.onProgress },
                );
                return resData || [];
            } finally {
                progress.done();
            }
        },
        enabled: !!isApaleoAuthed && userRole !== 'guest',
        staleTime: 0,
        retry: 1,
    });

    // Sync apiError from TanStack Query error
    useEffect(() => {
        if (reservationsError) {
            const isDateOrderError = reservationsError.message?.includes("422") && dashboardDateFrom > dashboardDateTo;
            setApiError(isDateOrderError ? "" : reservationsError.message);
            if (reservationsError.message?.includes("Token abgelaufen")) setIsApaleoAuthed(false);
        } else {
            setApiError("");
        }
    }, [reservationsError, dashboardDateFrom, dashboardDateTo, setIsApaleoAuthed]);

    // Handle Apaleo token expiry
    useEffect(() => {
        if (!isApaleoAuthed) return;
        let mounted = true;
        const checkToken = async () => {
            try {
                await pms.getToken();
            } catch (e) {
                if (mounted && e instanceof Error && e.message.includes("Token abgelaufen")) {
                    setIsApaleoAuthed(false);
                }
            }
        };
        checkToken();
        return () => { mounted = false; };
    }, [isApaleoAuthed, setIsApaleoAuthed]);

    const handleNavClick = (tab: string) => {
        navigate(tab === "dashboard" ? "/" : `/${tab}`);
        setIsMobileMenuOpen(false);
    };

    const handleOpenItem = (id: string, type: string, entityType?: string) => {
        if (type === "storno-conflict") {
            // D-Edge storno conflict: id is a RESERVATION id — open the
            // guest profile on the reservations table (useDeepLinkReservation
            // fetches out-of-window ids via pms.fetchReservationById).
            navigate(`/reservations?guest=${encodeURIComponent(id)}`);
        } else if (type === "lost_and_found") {
            navigate(`/fundsachen/${id}`);
        } else if (type === "hr_absence" || type === "hr_request") {
            navigate(`/hr/${id}`);
        } else if (type === "support-ticket-reply") {
            // Open the Support modal directly to the ticket that the admin replied to.
            setGlobalSupportTicketIdToOpen(id);
            setIsSupportOpen(true);
        } else if (type === "chat-reply") {
            // Route a chat reply notification to the correct entity surface.
            if (entityType === "fundsachen") {
                navigate(`/fundsachen/${id}`);
            } else if (entityType === "ticket") {
                setGlobalSupportTicketIdToOpen(id);
                setIsSupportOpen(true);
            } else {
                navigate(`/traces/${id}`);
            }
        } else {
            navigate(`/traces/${id}`);
        }
        setIsHistoryOpen(false);
    };

    const currentTenantId = user?.tenantId;

    // ── Centralized realtime store (Phase 2) ────────────────────────
    // One channel per tenant. Invalidates TanStack Query cache on
    // postgres_changes events. Replaces 84 ad-hoc per-component channels.
    // Broadcast/presence/collaborative-lock channels remain in their
    // owning hooks/components (useRealtimeChat, KassenbuchView, etc.)
    useEffect(() => {
        if (!currentTenantId || !user?.id) return;
        const { start, stop } = createRealtimeStore(currentTenantId, user.id);
        start();
        return stop;
    }, [currentTenantId, user?.id]);

    const features = tenantConfig?.features;
    const featuresKey = useMemo(() => features ? Object.keys(features).join("|") : null, [features]);
    const isGlobalAdmin = userRole === 'admin' && !!user?.email && globalAdmins.includes(user.email.toLowerCase());
    let allowedTabs: string[] = useMemo(() => {
        const base = getRoleData(userRole)?.tabs || [];
        // `tenantConfig?.features` is captured (not narrowed) inside the callback,
        // so guard it explicitly; behavior identical to the previous inline filter.
        const cfg = tenantConfig?.features;
        return base.filter(tabId => {
            if (!cfg) return tabId !== 'iframe';
            if (tabId === 'iframe') {
                return !!(cfg.iframe || cfg.roompricegenie);
            }
            if (cfg[tabId] !== undefined) {
                return !!cfg[tabId];
            }
            return true;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole, featuresKey]);
    if (isGlobalAdmin && !allowedTabs.includes("admin")) {
        allowedTabs = [...allowedTabs, "admin"];
    }

    // Messenger unread count for the sidebar bubble. `enabled` is gated on the
    // user actually having the chat tab, so users without it never issue the
    // query. Shares the query cache with ChatView (no duplicate fetch).
    const { unreadCount: chatUnreadCount } = useChatUnread({
        tenantId: user?.tenantId,
        myUserId: user?.id,
        enabled: allowedTabs.includes("chat"),
    });

    const isEffectiveCollapsed = isSidebarCollapsed && !isMobileMenuOpen;

    return (
        <div className="hub-main-layout text-black dark:text-foreground font-sans overflow-hidden print:h-auto print:overflow-visible">
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/50 z-200 md:hidden transition-opacity"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            <AppSidebar
                t={t}
                user={user}
                allowedTabs={allowedTabs}
                activeTab={activeTab}
                handleNavClick={handleNavClick}
                expandedGroups={expandedGroups}
                toggleGroup={toggleGroup}
                hasUnreadSupportTickets={hasUnreadSupportTickets}
                hasPendingHandover={hasPendingHandover}
                isEffectiveCollapsed={isEffectiveCollapsed}
                isSidebarCollapsed={isSidebarCollapsed}
                setIsSidebarCollapsed={setIsSidebarCollapsed}
                isMobileMenuOpen={isMobileMenuOpen}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
                setIsPreferencesOpen={setIsPreferencesOpen}
                hasNewPatchNote={hasNewPatchNote}
                handleOpenPatchNotes={handleOpenPatchNotes}
                getRoleData={getRoleData}
                userRole={userRole}
                signOut={signOut}
                chatUnreadCount={chatUnreadCount}
                tenantConfig={tenantConfig}
            />

            <div className="flex-1 flex flex-col h-dvh max-h-dvh overflow-hidden print:h-auto print:overflow-visible">
                {isOffline && (
                    <div className="bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest text-center py-2 px-4 shadow-md z-50 flex justify-center items-center gap-2 shrink-0">
                        <Globe size={14} className="opacity-70" />
                        Offline-Modus: Änderungen werden lokal gespeichert und bei Verbindung synchronisiert.
                    </div>
                )}
                <AppHeader
                    t={t}
                    activeTab={activeTab}
                    user={user}
                    allowedTabs={allowedTabs}
                    tenantConfig={tenantConfig}
                    formattedDate={formattedDate}
                    clockStatus={clockStatus}
                    clockKey={clockKey}
                    clockLabels={clockLabels}
                    clockColors={clockColors}
                    badgeBg={badgeBg}
                    displayMs={displayMs}
                    formatClockTime={formatClockTime}
                    unreadCount={unreadCount}
                    chatUnreadCount={chatUnreadCount}
                    requestPushPermission={requestPushPermission}
                    getRoleData={getRoleData}
                    userRole={userRole}
                    hasUnreadUserReplies={hasUnreadUserReplies}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                    setIsPreferencesOpen={setIsPreferencesOpen}
                    setIsSupportOpen={setIsSupportOpen}
                    setIsHistoryOpen={setIsHistoryOpen}
                    handleNavClick={handleNavClick}
                />

                <main className={`flex-1 overflow-x-hidden ${location.pathname.startsWith('/chat') ? 'flex flex-col pt-[10px] pb-2 md:pt-[10px] md:pb-3.5 md:pl-1 md:pr-3.5 overflow-hidden bg-bg min-h-0' : 'overflow-y-auto bg-bg/50 p-2 md:px-6 md:py-8'} print:p-0 print:overflow-visible`}>
                    {apiError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                            <AlertTriangle size={16} /> API Warnung: {apiError}
                        </div>
                    )}

                    <div className={`w-full ${location.pathname.startsWith('/chat') ? 'flex-1 flex flex-col min-h-0 h-full' : ''} print:max-w-none print:mx-0`}>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => {
                                // Stale-deploy chunk error: auto-reload (rate-limited)
                                // — after the reload the new service worker serves
                                // the new chunks, fixing the "F5 required" issue.
                                if (isChunkLoadError(error) && recoverFromChunkError()) {
                                    return (
                                        <div className="flex flex-col items-center justify-center py-40 animate-in fade-in">
                                            <Loader2 className="animate-spin text-[#859F4A] mb-4" size={48} />
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted">{t('common.errors.updateLoading')}</p>
                                        </div>
                                    );
                                }
                                return (
                                    <div className="flex flex-col items-center justify-center py-40 px-8 text-center">
                                        <p className="text-2xl font-black text-fg mb-2">
                                            {t('common.errors.somethingWentWrong')}
                                        </p>
                                        <p className="text-sm text-muted mb-6 max-w-md">
                                            {(error as any)?.message || t('common.errors.unexpected')}
                                        </p>
                                        {isChunkLoadError(error) ? (
                                            <button
                                                onClick={() => window.location.reload()}
                                                className="px-6 py-3 bg-accent text-white rounded-xl font-bold text-sm hover:opacity-80 transition-opacity"
                                            >
                                                {t('common.errors.reloadPage')}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={resetError}
                                                className="px-6 py-3 bg-accent text-white rounded-xl font-bold text-sm hover:opacity-80 transition-opacity"
                                            >
                                                {t('common.errors.tryAgain')}
                                            </button>
                                        )}
                                    </div>
                                );
                            }}
                        >
                            <React.Suspense fallback={
                                <div className="flex flex-col items-center justify-center py-40 animate-in fade-in">
                                    <Loader2 className="animate-spin text-[#859F4A] mb-4" size={48} />
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted">{t('common.loading', 'Wird geladen…')}</p>
                                </div>
                            }>
                                <Routes>
                                    {allowedTabs.includes("dashboard") && (
                                        <Route
                                            path="/"
                                            element={
                                                <DashboardView
                                                    reservations={reservations}
                                                    user={user}
                                                    isLoading={isLoadingReservations}
                                                    dateFrom={dashboardDateFrom}
                                                    setDateFrom={setDashboardDateFrom}
                                                    dateTo={dashboardDateTo}
                                                    setDateTo={setDashboardDateTo}
                                                    dateFilterType={dashboardDateFilter}
                                                    setDateFilterType={setDashboardDateFilter}
                                                    searchTerm={dashboardSearchTerm}
                                                    setSearchTerm={setDashboardSearchTerm}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("reservations") && (
                                        <Route
                                            path="/reservations"
                                            element={
                                                <ReservationsRoute
                                                    user={user}
                                                    isApaleoAuthed={isApaleoAuthed}
                                                    onTokenExpired={() => setIsApaleoAuthed(false)}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("explorer") && (
                                        <Route
                                            path="/explorer"
                                            element={<ExplorerView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("roomrack") && (
                                        <Route
                                            path="/roomrack"
                                            element={
                                                <RoomRackView user={user} isGlobalLoading={isLoadingReservations} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("availability") && (
                                        <Route
                                            path="/availability"
                                            element={<AvailabilityView />}
                                        />
                                    )}
                                    {allowedTabs.includes("analytics") && (
                                        <Route
                                            path="/analytics"
                                            element={
                                                <AnalyticsView user={user} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("handover") && (
                                        <Route
                                            path="/handover"
                                            element={<HandoverView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("chat") && (
                                        <Route
                                            path="/chat"
                                            element={<ChatView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("traces") && (
                                        <Route
                                            path="/traces/:traceId?"
                                            element={
                                                <TracesView
                                                    user={user}
                                                    reservations={reservations}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("hk") && (
                                        <Route
                                            path="/hk"
                                            element={
                                                <HousekeepingView
                                                    user={user}
                                                    dateFrom={dashboardDateFrom}
                                                    setDateFrom={setDashboardDateFrom}
                                                    dateTo={dashboardDateTo}
                                                    setDateTo={setDashboardDateTo}
                                                    reservations={reservations}
                                                    isLoading={isLoadingReservations}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("ht") && (
                                        <Route
                                            path="/ht"
                                            element={
                                                <HaustechnikView
                                                    user={user}
                                                    reservations={reservations}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("internalresources") && (
                                        <Route
                                            // Path MUST equal the sidebar tab id: handleNavClick
                                            // navigates to `/${tabId}`, and an unmatched path falls
                                            // into the "*" catch-all → redirect to the dashboard.
                                            path="/internalresources"
                                            element={
                                                <InternalResourcesView
                                                    user={user}
                                                    reservations={reservations}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("hr") && (
                                        <Route
                                            path="/hr/:requestId?"
                                            element={
                                                <HRView
                                                    user={{ ...user, tenantId: user.tenantId ?? undefined }}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("sales") && (
                                        <Route
                                            path="/sales"
                                            element={<SalesView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("fundsachen") && (
                                        <Route
                                            path="/fundsachen/:fundsacheId?"
                                            element={
                                                <FundsachenView
                                                    user={user}
                                                    reservations={reservations}
                                                />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("logs") && (
                                        <Route
                                            path="/logs"
                                            element={<SystemLogsView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("users") && (
                                        <Route
                                            path="/users"
                                            element={
                                                <UsersView currentUser={user} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("admin") && (
                                        <Route
                                            path="/admin"
                                            element={<AdminDashboardView user={{ ...user, tenantId: user.tenantId ?? undefined }} />}
                                        />
                                    )}
                                    {allowedTabs.includes("automations") && (
                                        <Route
                                            path="/automations"
                                            element={<AutomationsView user={user} />}
                                        />
                                    )}
                                    {/* Workflows = user-facing No-Code Automation Builder
                                        (Wenn→dann rules); separate from the script-based
                                        /automations menu. Visible per ROLES.tabs (admin,
                                        management, sales, reception); edit rights inside
                                        stay admin/management. */}
                                    {allowedTabs.includes("workflows") && (
                                        <Route
                                            path="/workflows"
                                            element={<WorkflowsView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("kassenbuch") && (
                                        <Route
                                            path="/kassenbuch"
                                            element={
                                                <KassenbuchView user={user} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("buchhaltung") && (
                                        <Route
                                            path="/buchhaltung/*"
                                            element={<BuchhaltungView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("xrechnung") && (
                                        <Route
                                            path="/xrechnung"
                                            element={<XRechnungView user={user} />}
                                        />
                                    )}
                                    {allowedTabs.includes("anleitungen") && (
                                        <Route
                                            path="/anleitungen"
                                            element={
                                                <AnleitungenView user={user} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("inventory") && (
                                        <Route
                                            path="/inventory"
                                            element={
                                                <InventoryView user={user} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("groups") && (
                                        <Route
                                            path="/groups"
                                            element={
                                                <GroupsView user={user} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("offers") && (
                                        <Route
                                            path="/offers/:offerId?"
                                            element={
                                                <OffersView user={user} />
                                            }
                                        />
                                    )}
                                    {allowedTabs.includes("iframe") && (
                                        <Route
                                            path="/iframe"
                                            element={
                                                <div className="w-full h-[calc(100dvh-80px)] lg:h-[calc(100vh-144px)] rounded-[3.5rem] overflow-hidden border border-border shadow-xl bg-surface">
                                                    <iframe
                                                        src={getSafeIframeUrl(tenantConfig?.customIframeUrl)}
                                                        className="w-full h-full border-0"
                                                        title={tenantConfig?.customIframeName || "Partner App"}
                                                        allow="fullscreen"
                                                        sandbox="allow-scripts allow-same-origin allow-forms"
                                                    />
                                                </div>
                                            }
                                        />
                                    )}
                                    <Route path="*" element={<Navigate to="/" />} />
                                </Routes>
                            </React.Suspense>
                        </Sentry.ErrorBoundary>
                    </div>
                </main>
            </div>
            <Analytics />
            <ToastProvider />

            <NotificationListener currentUser={user} />
            {isHistoryOpen && (
                <NotificationHistoryDrawer
                    currentUser={user}
                    onClose={() => setIsHistoryOpen(false)}
                    onOpenItem={handleOpenItem}
                />
            )}
            {/* The lazy shell modals below must render inside a Suspense
                boundary (React.lazy requirement). While closed each component
                returns null — and while the chunk is still loading, the
                Suspense fallback (null) renders instead, so first-open shows
                the modal as soon as the chunk resolves. */}
            <React.Suspense fallback={null}>
                <PatchNotesModal
                    isOpen={isPatchNotesOpen}
                    onClose={() => setIsPatchNotesOpen(false)}
                />
                <UserProfilePreferencesModal
                    user={user}
                    tenantConfig={tenantConfig as Record<string, unknown> | null}
                    setTenantConfig={
                        // UserProfilePreferencesModal expects an updater-style setter;
                        // this app stores TenantConfig as a plain object, so wrap it.
                        setTenantConfig as unknown as (updater: (prev: Record<string, any> | null) => Record<string, any> | null) => void
                    }
                    isOpen={isPreferencesOpen}
                    onClose={() => setIsPreferencesOpen(false)}
                />

                <SupportTicketModal
                    isOpen={isSupportOpen}
                    onClose={() => setIsSupportOpen(false)}
                    user={user}
                    activeTenantId={currentTenantId}
                    ticketIdToOpen={globalSupportTicketIdToOpen}
                    onTicketOpened={() => setGlobalSupportTicketIdToOpen(null)}
                />

                {/* In-app attachment viewer — store-driven, no props needed.
                    Renders null while fileViewer is null. */}
                <FileViewerModal />
            </React.Suspense>
        </div>
    );
}


